import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Tabs,
  Table,
  Tag,
  Input,
  Row,
  Col,
  Card,
  Progress,
  Select,
  Alert,
} from "antd";

const { TabPane } = Tabs;
const { Search } = Input;

export default function PayrollDetailsModal({
  open,
  onClose,
  data = [],
  summary = {},
  defaultTab = "completed",
}) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (open) {
        let tab = defaultTab;

        if (tab === "completed" && summary.fullyPaid === 0) {
        tab = summary.partial > 0 ? "partial" : "unpaid";
        }

        if (tab === "partial" && summary.partial === 0) {
        tab = summary.unpaid > 0 ? "unpaid" : "completed";
        }        

        setActiveTab(tab);
        setSearch("");
        setStatusFilter("all");
    }
    }, [open, defaultTab, summary]);

  // Filter logic
  const filteredData = useMemo(() => {
  let list = Array.isArray(data) ? data : []; // ✅ safety

  if (activeTab !== "all") {
    list = list.filter((d) => d.status === activeTab);
  }

  if (statusFilter !== "all") {
    list = list.filter((d) => d.status === statusFilter);
  }

  if (search) {
    list = list.filter((d) =>
      d.employee_name.toLowerCase().includes(search.toLowerCase())
    );
  }

  return list;
}, [data, activeTab, search, statusFilter]);

  // 📋 Table columns
  const columns = [
    {
      title: "Employee",
      dataIndex: "employee_name",
      key: "employee_name",
    },
    {
      title: "Salary",
      dataIndex: "total_monthly_salary",
      sorter: (a, b) =>
        a.total_monthly_salary - b.total_monthly_salary,
    },
    {
      title: "Paid",
      dataIndex: "total_paid",
      sorter: (a, b) => a.total_paid - b.total_paid,
    },
    {
      title: "Difference",
      dataIndex: "difference",
      render: (val) => (
        <span style={{ color: val < 0 ? "red" : "green" }}>
          {val}
        </span>
      ),
      sorter: (a, b) => a.difference - b.difference,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => {
        const colorMap = {
            completed: "green",
            partial: "orange",
            unpaid: "red",
            no_salary: "gray",   
        };
        return <Tag color={colorMap[status]}>
            {status === "no_salary" ? "No Salary Set" : status}
        </Tag>;
      },
    },
  ];

  return (
    <Modal
        title="Payroll Details"
        open={open}
        onCancel={onClose}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        styles={{
            body: {
            maxHeight: "75vh",
            overflowY: "auto",
            },
        }}
    >

        {summary.noSalary > 0 && (
            <div
            className="salary-banner"
                style={{
                    marginBottom: 12,
                    padding: "8px 12px",
                    background: "#fffbe6",
                    border: "1px solid #ffe58f",
                    borderRadius: 6,
                    fontSize: 13,
                }}
            >
            ⚠️ {summary.noSalary} employees don’t have salary configured
            </div>
        )}
      {/* 📊 Summary */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={12} md={6}>
          <Card size="small">Total: {summary.total}</Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small">🟢 Paid: {summary.fullyPaid}</Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small">🟡 Partial: {summary.partial}</Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small">🔴 Unpaid: {summary.unpaid}</Card>
        </Col>
      </Row>

      {/* 📊 Progress */}
      <div style={{ marginBottom: 20 }}>
        <Progress percent={Number(summary.progress || 0)} />
      </div>

      {/* 🔍 Filters */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Search
            placeholder="Search employee"
            onChange={(e) => setSearch(e.target.value)}
            allowClear
          />
        </Col>
        <Col span={12}>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: "100%" }}
          >
            <Select.Option value="all">All Status</Select.Option>
            <Select.Option value="completed">Completed</Select.Option>
            <Select.Option value="partial">Partial</Select.Option>
            <Select.Option value="unpaid">Unpaid</Select.Option>
            <Select.Option value="no_salary">No Salary</Select.Option>
          </Select>
        </Col>
      </Row>

      {/* 📑 Tabs */}
      <Tabs 
      activeKey={activeTab} 
      onChange={setActiveTab} 
      tabBarGutter={16}
      moreIcon="..."
      tabPosition="top"
      >
        <TabPane tab="All" key="all" />
        <TabPane tab="Completed 🟢" key="completed" />
        <TabPane tab="Partial 🟡" key="partial" />
        <TabPane tab="Unpaid 🔴" key="unpaid" />
        <TabPane tab="No Salary ⚪" key="no_salary" />
      </Tabs>

      {/* 📋 Table */}
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="employee_id"
        pagination={{ pageSize: 6 }}
        size="small"
        scroll={{ x: 600 }}
        rowClassName={(record) =>
          record.status === "no_salary" ? "row-warning" : ""
        }
      />
    </Modal>
  );
}